import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Inbox, UserCheck } from 'lucide-react';
import { createElement } from 'react';
import {
  subscribeToOSChanges,
  subscribeToOSAceitasFor,
  unsubscribeChannel,
  type OSRow,
  type RealtimePostgresInsertPayload,
  type RealtimePostgresUpdatePayload,
} from '@/services/realtime-os.service';

interface RealtimeOSOptions {
  onNovaOSDisponivel?: (numeroOS: string) => void;
  onOSAceita?: (numeroOS: string, prestadorNome?: string) => void;
  enabled?: boolean;
}

/**
 * Hook para escutar mudanças em tempo real nas ordens de serviço.
 * Notifica prestadores quando novas OS ficam disponíveis.
 */
export function useRealtimeOSDisponiveis(options: RealtimeOSOptions = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enabled = true } = options;

  // Ref para armazenar IDs de OS já notificadas (evita duplicatas)
  const notifiedOSRef = useRef<Set<string>>(new Set());

  const handleInsert = useCallback((payload: RealtimePostgresInsertPayload<OSRow>) => {
    const newOS = payload.new;

    // Se a OS foi criada sem prestador, é uma nova OS disponível
    if (newOS.prestador_id === null && newOS.status === 'pendente') {
      // Evitar notificar a mesma OS múltiplas vezes
      if (!notifiedOSRef.current.has(newOS.id)) {
        notifiedOSRef.current.add(newOS.id);

        toast.success(t('myServices.realtime.newOS'), {
          description: t('myServices.realtime.newOSDescription'),
          icon: createElement(Inbox, { className: 'h-4 w-4' }),
          duration: 5000,
        });

        options.onNovaOSDisponivel?.(newOS.numero_os);
      }
    }

    // Invalidar cache para atualizar listas
    queryClient.invalidateQueries({ queryKey: ['ordens-servico', 'disponiveis'] });
    queryClient.invalidateQueries({ queryKey: ['agenda'] });
  }, [queryClient, options, t]);

  const handleUpdate = useCallback((payload: RealtimePostgresUpdatePayload<OSRow>) => {
    const newOS = payload.new;
    const oldOS = payload.old;

    // Se a OS tinha prestador e agora não tem, ficou disponível novamente
    if (oldOS.prestador_id !== null && newOS.prestador_id === null && newOS.status === 'pendente') {
      if (!notifiedOSRef.current.has(newOS.id)) {
        notifiedOSRef.current.add(newOS.id);

        toast.success(t('myServices.realtime.osAvailableAgain'), {
          description: t('myServices.realtime.osAvailableAgainDescription'),
          icon: createElement(Inbox, { className: 'h-4 w-4' }),
          duration: 5000,
        });

        options.onNovaOSDisponivel?.(newOS.numero_os);
      }
    }

    // Se a OS foi aceita por outro prestador, remover da lista e notificar
    if (oldOS.prestador_id === null && newOS.prestador_id !== null) {
      // Remover do set de notificações (pode ficar disponível de novo no futuro)
      notifiedOSRef.current.delete(newOS.id);

      toast.info(t('myServices.realtime.osAccepted'), {
        description: t('myServices.realtime.osAcceptedDescription'),
        icon: createElement(UserCheck, { className: 'h-4 w-4' }),
        duration: 3000,
      });

      options.onOSAceita?.(newOS.numero_os);
    }

    // Se OS foi cancelada ou status mudou para não-pendente
    if (newOS.status !== 'pendente' && oldOS.status === 'pendente') {
      notifiedOSRef.current.delete(newOS.id);
    }

    // Invalidar cache para atualizar listas
    queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
    queryClient.invalidateQueries({ queryKey: ['agenda'] });
  }, [queryClient, options, t]);

  useEffect(() => {
    if (!enabled) return;

    const channel = subscribeToOSChanges({ onInsert: handleInsert, onUpdate: handleUpdate });

    return () => {
      if (import.meta.env.DEV) {
        console.log('🔴 Realtime: Desconectando canal ordens_servico');
      }
      unsubscribeChannel(channel);
    };
  }, [enabled, handleInsert, handleUpdate]);
}

/**
 * Hook para admins/atendentes escutarem quando uma OS é aceita por um prestador.
 */
export function useRealtimeOSAceitas(responsavelId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (!responsavelId) return;

    const channel = subscribeToOSAceitasFor(responsavelId, (payload) => {
      const newOS = payload.new;
      const oldOS = payload.old;

      // Se a OS foi aceita (tinha prestador_id null e agora tem)
      if (oldOS.prestador_id === null && newOS.prestador_id !== null) {
        toast.success(t('myServices.realtime.osAcceptedByProvider'), {
          description: t('myServices.realtime.osAcceptedByProviderDescription', { number: newOS.numero_os }),
          icon: createElement(UserCheck, { className: 'h-4 w-4' }),
          duration: 5000,
        });
      }

      // Invalidar cache
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    });

    return () => {
      unsubscribeChannel(channel);
    };
  }, [responsavelId, queryClient, t]);
}
